import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Mail, Lock, Fingerprint, Wand2, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { GoogleIcon } from '@/components/icons/GoogleIcon'
import { API_BASE_URL } from '@/lib/config'
import { marketingUrl } from '@/lib/marketing'
import { defaultLocale } from '@/lib/locales'
import { useAuthStore } from '@/stores/useAuthStore'
import { ApiHttpError, login, requestMagicLink, verifyMagicCode } from '@/api/auth'
import { isPasskeySupported, loginWithPasskey } from '@/api/passkeys'
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { isDesktopShell, openExternalUrl } from '@/lib/desktopShell'
import { FRONTEND_URL } from '@/lib/config'
import { enParametres as provenanceEnParametres } from '@/lib/provenance'

const loginSchema = z.object({
  email: z.string().min(1, 'Email requis').email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
})

type LoginForm = z.infer<typeof loginSchema>

export function LoginPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  useDocumentTitle(t('login.pageTitle', 'Connexion'))

  const clearAuth = useAuthStore((s) => s.clear)
  const setTokens = useAuthStore((s) => s.setTokens)
  const postLoginRedirect = usePostLoginRedirect()

  const [infoMessage, setInfoMessage] = useState('')
  const [accountDeletedError, setAccountDeletedError] = useState('')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  useEffect(() => {
    if (localStorage.getItem('callizy_account_deleted') === 'true') {
      localStorage.removeItem('callizy_account_deleted')
      setAccountDeletedError(
        "Votre compte a été supprimé. Veuillez contacter votre manager pour plus d'informations.",
      )
      return
    }
    // Relay OAuth depuis Callizy.app : on a été ouvert dans Safari pour que
    // l'user bénéficie de ses cookies Google. On marque la session pour qu'au
    // callback on redirige vers /auth/desktop-handoff (Universal Link) au
    // lieu de naviguer vers le dashboard dans Safari.
    const oauthRelay = new URLSearchParams(window.location.search).get('desktop_oauth')
    if (oauthRelay === 'google' && !isDesktopShell()) {
      sessionStorage.setItem('desktop_oauth_mode', 'google')
      localStorage.setItem('preferred_locale', locale)
      // Auto-trigger : pas besoin de cliquer le bouton — on inline le flow
      // OAuth init + redirect pour éviter le forward-reference vers
      // startGoogleAuth() défini plus bas.
      void fetch(
        `${API_BASE_URL}/api/auth/oauth-init/?frontend_url=${encodeURIComponent(window.location.origin)}`,
        { credentials: 'include' },
      ).finally(() => {
        window.location.href = `${API_BASE_URL}/accounts/google/login/?locale=${locale}`
      })
      return
    }

    // Relay OAuth pour l'app NATIVE iOS (ASWebAuthenticationSession). Parallèle
    // au desktop_oauth du Mac mais distinct : au callback on ne consommera PAS
    // le code, on le transmettra à l'app via le scheme `callizy://auth/native`
    // (path distinct du `/handoff` macOS) qui l'échangera nativement.
    const nativeOauth = new URLSearchParams(window.location.search).get('native_oauth')
    if (nativeOauth === 'google' && !isDesktopShell()) {
      sessionStorage.setItem('native_oauth_mode', 'google')
      localStorage.setItem('preferred_locale', locale)
      // appli=1: the server creates no new account from the app (no plan is sold there),
      // except from "Create an account" in the iPhone app that sells the plan (inscription=1).
      const inscription = new URLSearchParams(window.location.search).get('inscription') === '1'
      void fetch(
        `${API_BASE_URL}/api/auth/oauth-init/?frontend_url=${encodeURIComponent(window.location.origin)}&appli=1${inscription ? '&inscription=1' : ''}`,
        { credentials: 'include' },
      ).finally(() => {
        window.location.href = `${API_BASE_URL}/accounts/google/login/?locale=${locale}`
      })
      return
    }
    // Passkey natif iOS : pas de round-trip OAuth, tout est client-side. On lance
    // le passkey (Face ID dans la feuille Safari), puis on génère un temp-code via
    // /api/auth/native-handoff/ qu'on transmet à l'app via le même scheme.
    if (nativeOauth === 'passkey' && !isDesktopShell()) {
      localStorage.setItem('preferred_locale', locale)
      void (async () => {
        try {
          const tokens = await loginWithPasskey()
          const res = await fetch(`${API_BASE_URL}/api/auth/native-handoff/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokens.access}`,
            },
          })
          if (!res.ok) throw new Error('handoff failed')
          const { code } = (await res.json()) as { code: string }
          window.location.href = `callizy://auth/native?code=${encodeURIComponent(code)}`
        } catch {
          window.location.href = 'callizy://auth/native?error=passkey_failed'
        }
      })()
      return
    }
    const stored = sessionStorage.getItem('login_message')
    if (stored) {
      setInfoMessage(stored)
      sessionStorage.removeItem('login_message')
      return
    }
    const params = new URLSearchParams(window.location.search)
    const reason = params.get('reason')
    if (reason === 'expired') setInfoMessage(t('auth.login.sessionExpired'))
    else if (reason === 'invalid') setInfoMessage(t('auth.login.sessionInvalid'))
    else if (reason === 'unauthorized') setInfoMessage(t('auth.login.authRequired'))
  }, [t])

  const loginMutation = useMutation({
    mutationFn: async (input: LoginForm) => {
      const tokens = await login(input)
      clearAuth()
      setTokens(tokens.access, tokens.refresh)
      await postLoginRedirect(locale)
      window.dispatchEvent(new Event('login-complete'))
    },
    onError: (error) => {
      if (error instanceof ApiHttpError) {
        if (error.status === 403) {
          const data = error.data as { error?: string; email?: string }
          if (data?.error === 'email_not_verified' && data?.email) {
            window.location.replace(
              `/${locale}/verify-email?email=${encodeURIComponent(data.email)}&from=login`,
            )
            return
          }
          toast.error(t('auth.login.accessDenied', 'Accès refusé'))
          return
        }
        if (error.status === 401) {
          toast.error(t('auth.login.invalidCredentials', 'Email ou mot de passe incorrect'))
          return
        }
        toast.error(t('auth.login.loginError', 'Erreur de connexion'))
        return
      }
      console.error('[Login]', error)
      toast.error(t('auth.login.serverError', 'Erreur de connexion au serveur'))
    },
  })

  const onSubmit = handleSubmit((values) => loginMutation.mutate(values))

  // Détection un seul rendu : pas de re-check à chaque keystroke
  const passkeySupported = useMemo(() => isPasskeySupported(), [])

  // Magic link : modal en 2 étapes (email → code)
  const [magicOpen, setMagicOpen] = useState(false)
  const [magicStep, setMagicStep] = useState<'email' | 'code'>('email')
  const [magicEmail, setMagicEmail] = useState('')
  const [magicCode, setMagicCode] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)

  const magicLinkMutation = useMutation({
    mutationFn: (email: string) => requestMagicLink(email, locale),
    onSuccess: () => {
      setMagicStep('code')
      setMagicCode('')
      setResendCooldown(60)
    },
    onError: () => {
      toast.error(
        t('auth.login.magicError', "Impossible d'envoyer le code. Réessayez."),
      )
    },
  })

  const verifyCodeMutation = useMutation({
    mutationFn: () => verifyMagicCode(magicEmail, magicCode),
    onSuccess: async (tokens) => {
      clearAuth()
      setTokens(tokens.access, tokens.refresh)
      setMagicOpen(false)
      setMagicStep('email')
      setMagicCode('')
      await postLoginRedirect(locale)
      window.dispatchEvent(new Event('login-complete'))
    },
    onError: (error) => {
      if (error instanceof ApiHttpError) {
        if (error.status === 403) {
          const data = error.data as { error?: string; email?: string }
          if (data?.error === 'email_not_verified' && data?.email) {
            window.location.replace(
              `/${locale}/verify-email?email=${encodeURIComponent(data.email)}&from=magic`,
            )
            return
          }
        }
        if (error.status === 400) {
          const data = error.data as { error?: string; remaining_attempts?: number }
          if (data?.error === 'too_many_attempts') {
            toast.error(
              t('auth.login.magicCodeTooMany', 'Trop de tentatives. Demandez un nouveau code.'),
            )
            setMagicStep('email')
            return
          }
          toast.error(t('auth.login.magicCodeInvalid', 'Code incorrect.'))
          setMagicCode('')
          return
        }
      }
      console.error('[MagicCode]', error)
      toast.error(t('auth.login.magicCodeError', 'Vérification impossible. Réessayez.'))
    },
  })

  // Compte à rebours pour le bouton "Renvoyer un code"
  useEffect(() => {
    if (resendCooldown <= 0) return
    const id = window.setInterval(() => {
      setResendCooldown((c) => Math.max(0, c - 1))
    }, 1000)
    return () => window.clearInterval(id)
  }, [resendCooldown])

  const openMagicModal = () => {
    const current = (watch('email') || '').trim()
    setMagicEmail(current)
    setMagicStep('email')
    setMagicCode('')
    setMagicOpen(true)
  }

  const closeMagicModal = () => {
    if (verifyCodeMutation.isPending || magicLinkMutation.isPending) return
    setMagicOpen(false)
    setMagicStep('email')
    setMagicCode('')
    setResendCooldown(0)
  }

  const submitMagicEmail = () => {
    const trimmed = magicEmail.trim()
    if (!trimmed) return
    magicLinkMutation.mutate(trimmed)
  }

  const submitMagicCode = () => {
    const digits = magicCode.replace(/\D/g, '')
    if (digits.length !== 6) return
    verifyCodeMutation.mutate()
  }

  const resendMagicCode = () => {
    if (resendCooldown > 0) return
    magicLinkMutation.mutate(magicEmail)
  }

  const passkeyLoginMutation = useMutation({
    mutationFn: async () => {
      const tokens = await loginWithPasskey()
      clearAuth()
      setTokens(tokens.access, tokens.refresh)
      await postLoginRedirect(locale)
      window.dispatchEvent(new Event('login-complete'))
    },
    onError: (error) => {
      // L'utilisateur a annulé le prompt système → silencieux
      if (
        error instanceof DOMException &&
        (error.name === 'NotAllowedError' || error.name === 'AbortError')
      ) {
        return
      }
      if (error instanceof ApiHttpError) {
        if (error.status === 403) {
          const data = error.data as { error?: string; email?: string }
          if (data?.error === 'email_not_verified' && data?.email) {
            window.location.replace(
              `/${locale}/verify-email?email=${encodeURIComponent(data.email)}&from=login`,
            )
            return
          }
        }
        if (error.status === 401) {
          toast.error(
            t('auth.login.passkeyUnknown', "Clé d'accès inconnue ou révoquée"),
          )
          return
        }
      }
      console.error('[Login passkey]', error)
      toast.error(
        t('auth.login.passkeyError', "Échec de la connexion par clé d'accès"),
      )
    },
  })

  const googleHref = `${API_BASE_URL}/accounts/google/login/?locale=${locale}`
  const isSubmitting =
    loginMutation.isPending ||
    passkeyLoginMutation.isPending ||
    magicLinkMutation.isPending ||
    verifyCodeMutation.isPending

  /**
   * Démarre le flow Google OAuth. Avant de rediriger vers allauth, on POSE
   * `oauth_frontend_url` en session Django (via /api/auth/oauth-init/) pour
   * que le callback post-Google sache renvoyer vers app.callizy.ca et pas
   * vers le Next (callizy.ca) — le Referer étant perdu après le round-trip
   * Google.
   */
  const startGoogleAuth = async () => {
    localStorage.setItem('preferred_locale', locale)

    // Mode wrap macOS : on délègue le flow OAuth à Safari pour que l'user
    // bénéficie des cookies Google déjà présents (login en 1 clic). Le
    // login fini dans Safari fera un handoff via Universal Link
    // /auth/desktop-handoff qui réveille Callizy.app avec les tokens.
    if (isDesktopShell()) {
      const safariLoginUrl = `${FRONTEND_URL}/${locale}/login?desktop_oauth=google`
      openExternalUrl(safariLoginUrl)
      return
    }

    try {
      await fetch(
        // Origin of the visit: "Continue with Google" here also creates accounts.
        `${API_BASE_URL}/api/auth/oauth-init/?frontend_url=${encodeURIComponent(
          window.location.origin,
        )}${provenanceEnParametres()}`,
        { credentials: 'include' },
      )
    } catch {
      // On laisse passer : si oauth-init échoue, le backend fallback sur
      // Referer/Origin, et au pire FRONTEND_URL (callizy.ca).
    }
    window.location.href = googleHref
  }

  // App native iOS (ASWebAuthenticationSession) : le useEffect ci-dessus va
  // immédiatement rediriger vers la méthode pré-sélectionnée. On affiche un loader
  // plutôt que de laisser flasher le formulaire de login une fraction de seconde.
  const isNativeOauth =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('native_oauth')
  if (isNativeOauth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A192F] text-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white text-slate-900">
      <div className="relative hidden md:flex items-end bg-[#0A192F] text-white p-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(800px 400px at 20% 10%, #ffffff 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />{' '}
            {t('auth.login.securityBadge', 'Sécurité & simplicité')}
          </div>
          <h1 className="text-3xl font-semibold leading-tight">
            {t('auth.login.heroTitle') ||
              'Connectez-vous pour gérer vos appels professionnels'}
          </h1>
          <p className="text-white/80 max-w-md">
            {t('auth.login.heroDescription') ||
              'Un numéro pro, routage intelligent, messagerie vocale par e-mail. Tout est prêt en quelques minutes.'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-6 flex items-center gap-2">
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="h-7 w-7"
            />
            <span className="font-semibold">Callizy</span>
          </div>

          <h2 className="text-2xl font-semibold">{t('auth.login.title')}</h2>
          <p className="text-slate-600 text-sm mt-1">{t('auth.login.subtitle')}</p>

          {infoMessage && (
            <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
              {infoMessage}
            </div>
          )}
          {accountDeletedError && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              {accountDeletedError}
            </div>
          )}

          <button
            type="button"
            onClick={() => void startGoogleAuth()}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-900 hover:bg-slate-50"
          >
            <GoogleIcon className="h-5 w-5" />
            {t('auth.login.google')}
          </button>

          {passkeySupported && (
            <button
              type="button"
              onClick={() => passkeyLoginMutation.mutate()}
              disabled={isSubmitting}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-900 hover:bg-slate-50 disabled:opacity-50"
            >
              <Fingerprint className="h-5 w-5" />
              {t('auth.login.passkey', "Continuer avec une clé d'accès")}
            </button>
          )}

          <button
            type="button"
            onClick={openMagicModal}
            disabled={isSubmitting}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-900 hover:bg-slate-50 disabled:opacity-50"
          >
            <Wand2 className="h-5 w-5" />
            {t('auth.login.magicLink', 'Continuer avec un lien magique')}
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-500">{t('common.or', 'ou')}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email" className="text-slate-700">
                {t('auth.login.email')}
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  className="pl-8"
                  placeholder="vous@entreprise.ca"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  disabled={isSubmitting}
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">
                {t('auth.login.password')}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  className="pl-8"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  disabled={isSubmitting}
                  {...register('password')}
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl bg-black text-white py-2.5 font-medium hover:opacity-90 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.loading') : t('auth.login.button')}
            </Button>
          </form>

          <div className="mt-3 text-center">
            <Link
              to={`/${locale}/forgot-password`}
              className="text-sm text-slate-600 hover:text-[#0A192F] hover:underline"
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>

          <p className="mt-4 text-sm text-slate-600">
            {t('auth.login.noAccount')}{' '}
            <Link
              to={`/${locale}/signup`}
              className="text-[#0A192F] font-medium hover:underline"
            >
              {t('auth.login.signupLink')}
            </Link>
          </p>

          <p className="mt-2 text-xs text-slate-500">
            {t('auth.login.termsPrefix')}{' '}
            <a
              href={marketingUrl('/legal/terms', locale)}
              className="underline hover:text-slate-700"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('auth.login.termsLink')}
            </a>{' '}
            {t('auth.login.and')}{' '}
            <a
              href={marketingUrl('/legal/privacy', locale)}
              className="underline hover:text-slate-700"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('auth.login.privacyLink')}
            </a>
            .
          </p>
        </motion.div>
      </div>

      <Dialog
        open={magicOpen}
        onOpenChange={(o) => {
          if (!o) closeMagicModal()
          else setMagicOpen(true)
        }}
      >
        <DialogContent className="sm:max-w-md">
          {magicStep === 'email' ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('auth.login.magicModalTitle', 'Recevoir un code de connexion')}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    'auth.login.magicModalDesc',
                    "Saisissez votre email - nous vous envoyons un code à 6 chiffres et un lien magique.",
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-1.5">
                <Label htmlFor="magic-email" className="text-sm">
                  {t('auth.login.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="magic-email"
                    type="email"
                    className="pl-8"
                    placeholder="vous@entreprise.ca"
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitMagicEmail()
                    }}
                    autoFocus
                    disabled={magicLinkMutation.isPending}
                  />
                </div>
              </div>

              <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeMagicModal}
                  disabled={magicLinkMutation.isPending}
                >
                  {t('common.cancel', 'Annuler')}
                </Button>
                <Button
                  type="button"
                  onClick={submitMagicEmail}
                  disabled={magicLinkMutation.isPending || !magicEmail.trim()}
                  className="bg-black text-white hover:opacity-90"
                >
                  {magicLinkMutation.isPending
                    ? t('common.loading', 'Chargement...')
                    : t('auth.login.magicSendButton', 'Envoyer le code')}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('auth.login.magicCodeTitle', 'Saisissez votre code')}
                </DialogTitle>
                <DialogDescription>
                  {t('auth.login.magicCodeDesc', 'Code envoyé à')}{' '}
                  <span className="font-medium text-slate-900">{magicEmail}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Input
                  id="magic-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  placeholder="000 000"
                  value={magicCode}
                  onChange={(e) => setMagicCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitMagicCode()
                  }}
                  autoFocus
                  disabled={verifyCodeMutation.isPending}
                  className="text-center text-2xl tracking-[0.4em] font-mono h-14"
                />
                <p className="text-xs text-slate-500 text-center">
                  {t(
                    'auth.login.magicCodeHint',
                    'Ou cliquez le lien dans l\'email depuis n\'importe quel appareil.',
                  )}
                </p>
              </div>

              <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setMagicStep('email')
                    setMagicCode('')
                  }}
                  disabled={verifyCodeMutation.isPending}
                >
                  ← {t('auth.login.magicCodeBack', 'Changer d\'email')}
                </Button>
                <Button
                  type="button"
                  onClick={submitMagicCode}
                  disabled={
                    verifyCodeMutation.isPending ||
                    magicCode.replace(/\D/g, '').length !== 6
                  }
                  className="bg-black text-white hover:opacity-90"
                >
                  {verifyCodeMutation.isPending
                    ? t('common.loading', 'Chargement...')
                    : t('auth.login.magicCodeVerify', 'Vérifier')}
                </Button>
              </DialogFooter>

              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={resendMagicCode}
                  disabled={resendCooldown > 0 || magicLinkMutation.isPending}
                  className="text-xs text-slate-500 hover:text-slate-900 disabled:opacity-50"
                >
                  {resendCooldown > 0
                    ? t('auth.login.magicCodeResendIn', 'Renvoyer dans {{seconds}} s', {
                        seconds: resendCooldown,
                      })
                    : t('auth.login.magicCodeResend', 'Renvoyer un code')}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
